import type { ClientModuleRecord } from "../../clients/clients.types";
import {
  parseJsonArrayColumn,
  type ModuleCatalogRecord,
} from "../../modules/modules.types";
import type { ReadAiAssignment } from "../meetingIntelligence.types";

/**
 * Heuristic MODULE-MATCH RATING for the Review Queue's tracked-module picker —
 * "which of this client's modules does this action item probably belong to?"
 *
 * This is a reviewer aid, NOT identity resolution. The API's
 * `module-resolver.ts` stays the only resolver: it is conservative on purpose
 * (any ambiguity persists `moduleId: null` for a human), and this file is the
 * other half of that contract — it RANKS the client's modules for the human,
 * with a 0–1 rating, and never persists anything by itself. Applying a
 * suggestion goes through the ordinary `set-module` action, which the server
 * re-validates (live row, same client).
 */
export interface ModuleMatchRating {
  module: ClientModuleRecord;
  /** 0–1 heuristic rating; see the signal table in `rateModulesForAssignment`. */
  score: number;
  /** Human explanation of the winning signal (tooltip copy). */
  reason: string;
}

/** Minimum rating at which a candidate is surfaced as THE suggestion. */
export const SUGGESTION_MIN_SCORE = 0.5;

/**
 * Loose spelling key for heuristic comparison: lowercase, word-ish runs joined
 * by "-". This is deliberately NOT the API resolver's `normalizeModuleKey`
 * contract (see `governedSlugOf` in modules.types.ts — the web never re-derives
 * identity); it is a local match heuristic that may drift without changing what
 * the server resolves or accepts.
 */
const looseKey = (value: string | null | undefined): string =>
  (value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/** Word tokens of a spelling (length ≥ 2, so "of"/"a" noise drops out). */
const tokensOf = (value: string | null | undefined): string[] =>
  looseKey(value)
    .split("-")
    .filter((t) => t.length >= 2);

/** `phrase` present in `text` on word boundaries (both as loose keys). */
const containsPhrase = (textKey: string, phraseKey: string): boolean =>
  !!phraseKey && `-${textKey}-`.includes(`-${phraseKey}-`);

/**
 * Every spelling a catalog row answers to, as loose keys. Governed rows carry
 * the central alias table; the repo name and its `intrasync-`-stripped form are
 * included to mirror the two index keys the Coordinator publishes.
 */
const catalogSpellings = (row: ModuleCatalogRecord): string[] => {
  const out = new Set<string>();
  const add = (v: string | null | undefined) => {
    const k = looseKey(v);
    if (k) out.add(k);
  };
  add(row.moduleKey);
  add(row.displayName);
  add(row.governedRepo);
  if (row.governedRepo) add(row.governedRepo.replace(/^intrasync-/, ""));
  for (const alias of parseJsonArrayColumn(row.aliases)) add(alias);
  return [...out];
};

/** spelling (loose key) → the governed slugs claiming it. */
const buildSpellingIndex = (
  catalog: ModuleCatalogRecord[]
): Map<string, Set<string>> => {
  const index = new Map<string, Set<string>>();
  for (const row of catalog) {
    // Only governed rows anchor an identity — their moduleKey IS the slug.
    if (!row.governedRepo?.trim()) continue;
    const slug = looseKey(row.moduleKey);
    if (!slug) continue;
    for (const spelling of catalogSpellings(row)) {
      const set = index.get(spelling) ?? new Set<string>();
      set.add(slug);
      index.set(spelling, set);
    }
  }
  return index;
};

/** The governed slugs a client module's own spellings reach via the catalog. */
const slugsOfModule = (
  module: ClientModuleRecord,
  index: Map<string, Set<string>>
): Set<string> => {
  const out = new Set<string>();
  for (const spelling of [looseKey(module.moduleKey), looseKey(module.displayName)]) {
    for (const slug of index.get(spelling) ?? []) out.add(slug);
  }
  return out;
};

type AssignmentSignals = Pick<
  ReadAiAssignment,
  "title" | "context" | "governedSlug" | "governedRepo"
>;

/**
 * Rate every module against one assignment. Signals, strongest first (the
 * rating is the best signal that fires, never a sum):
 *
 *   0.95  the extraction's governed attribution (`governedSlug`) reaches this
 *         module through the catalog (aliases included)
 *   0.90  `governedSlug` loosely equals the module's own key/name (no catalog
 *         row needed — covers an unsynced or legacy vocabulary)
 *   0.75  the module's name (or a catalog alias) appears in the item TITLE
 *   0.55  …appears in the item CONTEXT
 *   0.50  every word of a multi-word module name appears somewhere in the text
 *   0.35  at least half the words of a multi-word module name appear
 *
 * Returns only modules that scored (> 0), best first, ties alphabetical.
 */
export function rateModulesForAssignment(
  assignment: AssignmentSignals,
  modules: ClientModuleRecord[],
  catalog: ModuleCatalogRecord[] = []
): ModuleMatchRating[] {
  const index = buildSpellingIndex(catalog);
  const governedSlug =
    looseKey(assignment.governedSlug) ||
    looseKey(assignment.governedRepo?.replace(/^intrasync-/, ""));

  const titleKey = looseKey(assignment.title);
  const contextKey = looseKey(assignment.context);
  const textTokens = new Set([
    ...tokensOf(assignment.title),
    ...tokensOf(assignment.context),
  ]);

  const ratings: ModuleMatchRating[] = [];

  for (const module of modules) {
    const moduleSlugs = slugsOfModule(module, index);

    // The names this module answers to in prose: its own two spellings plus
    // any catalog aliases of the identities it maps to.
    const names = new Set<string>([
      looseKey(module.moduleKey),
      looseKey(module.displayName),
    ]);
    for (const row of catalog) {
      if (!row.governedRepo?.trim()) continue;
      if (!moduleSlugs.has(looseKey(row.moduleKey))) continue;
      for (const spelling of catalogSpellings(row)) names.add(spelling);
    }
    names.delete("");

    let score = 0;
    let reason = "";
    const consider = (s: number, r: string) => {
      if (s > score) {
        score = s;
        reason = r;
      }
    };

    if (governedSlug) {
      if (moduleSlugs.has(governedSlug)) {
        consider(
          0.95,
          `Extraction attributed this item to the governed module "${governedSlug}"`
        );
      } else if (
        looseKey(module.moduleKey) === governedSlug ||
        looseKey(module.displayName) === governedSlug
      ) {
        consider(
          0.9,
          `Extraction attributed this item to "${governedSlug}", which matches this module's name`
        );
      }
    }

    if (score < 0.75) {
      for (const name of names) {
        if (containsPhrase(titleKey, name)) {
          consider(0.75, `"${module.displayName}" is named in the action item`);
          break;
        }
      }
    }
    if (score < 0.55 && contextKey) {
      for (const name of names) {
        if (containsPhrase(contextKey, name)) {
          consider(0.55, `"${module.displayName}" is named in the item's context`);
          break;
        }
      }
    }

    if (score < 0.5) {
      const nameTokens = tokensOf(module.displayName);
      if (nameTokens.length >= 2) {
        const hits = nameTokens.filter((t) => textTokens.has(t)).length;
        if (hits === nameTokens.length) {
          consider(0.5, `Every word of "${module.displayName}" appears in the item`);
        } else if (hits * 2 >= nameTokens.length) {
          consider(0.35, `Some words of "${module.displayName}" appear in the item`);
        }
      }
    }

    if (score > 0) ratings.push({ module, score, reason });
  }

  return ratings.sort(
    (a, b) =>
      b.score - a.score ||
      (a.module.displayName || "").localeCompare(b.module.displayName || "")
  );
}

/**
 * The extraction attributed the item to a governed module the client has NO
 * matching `client_modules` row for — the "customer wants a module they don't
 * have yet" signal (module-resolver.ts, B3). Returns the slug to surface, or
 * null. This is exactly the case where the picker LOOKS broken ("the new
 * module isn't in the dropdown"): the dropdown is scoped to the client's
 * assigned modules by API contract, so the fix is assigning the module in
 * Client 360, not a bigger dropdown.
 */
export function unassignedGovernedSlug(
  assignment: AssignmentSignals,
  modules: ClientModuleRecord[],
  catalog: ModuleCatalogRecord[] = []
): string | null {
  const slug =
    looseKey(assignment.governedSlug) ||
    looseKey(assignment.governedRepo?.replace(/^intrasync-/, ""));
  if (!slug) return null;
  const index = buildSpellingIndex(catalog);
  const owned = modules.some(
    (m) =>
      slugsOfModule(m, index).has(slug) ||
      looseKey(m.moduleKey) === slug ||
      looseKey(m.displayName) === slug
  );
  return owned ? null : assignment.governedSlug ?? slug;
}
